// @ts-nocheck
import React, { useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useStore } from "@tanstack/react-form";
import InlineDetailTable, {
  type DetailColumn,
} from "@/components/shared/InlineDetailTable";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useMaterialLookup } from "@/hooks/useFKLookup";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export function BOMBuilder({
  form,
  componentIndex,
  productionOrderId,
}: {
  form: any;
  componentIndex: number;
  productionOrderId?: string;
}) {
  const { options: materialLookup } = useMaterialLookup();
  const { hasPermission } = useRoleAccess();
  const selectedProductId = useStore(
    form.store,
    (s: any) => s.values.productId,
  );

  const { data: fullProduct, isLoading } = useQuery({
    queryKey: ["product-details", selectedProductId],
    queryFn: () =>
      selectedProductId
        ? trpcClient.products.getById.query({ id: selectedProductId })
        : null,
    enabled: !!selectedProductId,
  });

  const handleChange = (newRows: any[]) => {
    const normalizedRows = (newRows || []).map((r: any) => {
      const qty = Number(r.qtyRequired ?? r.quantity ?? 0);
      const wastePct = Number(r.wastagePercent ?? 0);
      const total = Math.ceil(qty * (1 + wastePct / 100));
      return {
        ...r,
        qtyRequired: qty,
        quantity: qty,
        wastagePercent: wastePct,
        totalQuantity: total,
        isUserCustomized: true,
      };
    });

    const currentComponents = form.getFieldValue("components") || [];
    const newComponents = [...currentComponents];
    const existingComp = newComponents[componentIndex] || {};
    newComponents[componentIndex] = {
      ...existingComp,
      // Giữ tên và vai trò hợp phần đã có (trước đây bị ghi đè thành "Main"/"Component N")
      isMain: existingComp.isMain ?? componentIndex === 0,
      componentName:
        existingComp.componentName || (componentIndex === 0 ? "Main" : `Hợp phần ${componentIndex + 1}`),
      bomItems: normalizedRows,
    };
    form.setFieldValue("components", newComponents);

    if (productionOrderId) {
      toast.loading("Đang lưu thay đổi vật tư...");
    }
  };

  const handleAutoFill = () => {
    if (!fullProduct || !fullProduct.productMaterials) return;

    const newItems = fullProduct.productMaterials
      .filter((m: any) => !m.isDeleted)
      .map((m: any) => ({
        materialId: m.materialId || "",
        materialCode: m.material?.materialId || "",
        materialName: m.material?.name || "",
        material: m.material,
        qtyRequired: m.quantity || 0,
        quantity: m.quantity || 0,
        wastagePercent: m.wastePercent || 0,
        unit: m.material?.unit || "Tờ",
        notes: m.note || "",
      }));

    handleChange(newItems);
  };

  const getColumns = useCallback(
    (): DetailColumn<any>[] => [
      {
        key: "materialId",
        label: "Vật tư (Kho / Mã tự sinh)",
        width: "min-w-[270px]",
        render: (value, row, index, updateRow) => {
          const displayLabel =
            row.material?.name ||
            (row.materialCode && row.materialName
              ? `${row.materialCode} - ${row.materialName}`
              : row.materialName || row.materialCode) ||
            materialLookup?.find((m: any) => m.id === (value || row.materialId))?.label;

          return (
            <div className="space-y-1">
              <SharedCombobox
                type="material"
                value={value || ""}
                valueLabel={displayLabel}
                onChange={(val, opt) => {
                  const orig = opt?.original || {};
                  const autoSpec = orig.specs || (orig.gsm ? `${orig.gsm} gsm` : "");
                  updateRow({
                    ...row,
                    materialId: val,
                    materialCode: orig.materialId || opt?.code || row.materialCode || "",
                    materialName: orig.name || opt?.label || row.materialName,
                    unit: orig.unit || row.unit || "Tờ",
                    specification: !row.specification ? autoSpec : row.specification,
                    isAutoGenerated: false,
                    isUserCustomized: true,
                  });
                }}
                hideLabel
              />
              {row.isAutoGenerated && (
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-800 border-amber-300"
                  >
                    Mã tự sinh: {row.materialCode}
                  </Badge>
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "routingStepId",
        label: "Công đoạn",
        width: "min-w-[150px]",
        render: (value, row, index, updateRow) => {
          const currentSteps =
            form.getFieldValue("components")?.[componentIndex]?.routingSteps || [];
          return (
            <select
              className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 shadow-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
              value={value || ""}
              onChange={(e) =>
                updateRow({
                  ...row,
                  routingStepId: e.target.value || null,
                  isUserCustomized: true,
                })
              }
            >
              <option value="">— Chung —</option>
              {currentSteps.map((step: any, sIdx: number) => (
                <option key={step.id || sIdx} value={step.id || step.stepId}>
                  {step.stepOrder || sIdx + 1}. {step.name || step.process?.name}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        key: "specification",
        label: "Quy cách / Khổ",
        width: "min-w-[160px]",
        type: "text",
      },
      {
        key: "qtyRequired",
        label: "Số lượng",
        width: "w-[115px]",
        type: "number",
      },
      {
        key: "unit",
        label: "Đơn vị",
        width: "w-[95px]",
        render: (value, row, index, updateRow) => (
          <Input
            className="h-8 text-xs"
            value={value || "Tờ"}
            onChange={(e) =>
              updateRow({ ...row, unit: e.target.value, isUserCustomized: true })
            }
          />
        ),
      },
      {
        key: "wastagePercent",
        label: "Bù hao (%)",
        width: "w-[110px]",
        type: "number",
      },
      {
        key: "notes",
        label: "Ghi chú / Quy đổi",
        width: "w-full min-w-[200px]",
        type: "text",
      },
    ],
    [hasPermission, form, componentIndex, materialLookup],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-slate-700">
          Định mức vật tư (BOM){" "}
          {componentIndex > 0 && `- Component ${componentIndex}`}
        </h4>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleAutoFill}
          disabled={isLoading || !fullProduct}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}{" "}
          Lấy từ Sản phẩm
        </Button>
      </div>

      <form.Subscribe
        selector={(state: any) =>
          state.values.components?.[componentIndex]?.bomItems
        }
      >
        {(bomItems: any[]) => (
          <InlineDetailTable
            columns={getColumns()}
            rows={bomItems || []}
            onChange={handleChange}
            createEmptyRow={() => ({
              materialId: "",
              materialCode: "",
              materialName: "",
              specification: "",
              qtyRequired: 0,
              quantity: 0,
              unit: "Tờ",
              wastagePercent: 0,
              notes: "",
              isUserCustomized: true,
            })}
            minRows={0}
          />
        )}
      </form.Subscribe>
    </div>
  );
}
